import { ApiError } from '@/lib/api'

/** The stored size (F1); the client crop only trims the upload, the server re-normalizes. */
export const IMAGE_SIDE = 512
export const MAX_IMAGE_MB = 5
export const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024

/**
 * Center-crop to a 512² JPEG when the browser can decode the file, else send the original for
 * the backend to decide (F1). The tiny canvas also dodges Safari's ~16.7 MP canvas limit.
 */
export async function downscaleImage(file: File): Promise<Blob> {
  if (typeof createImageBitmap !== 'function') return file
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return file
  }
  try {
    const canvas = document.createElement('canvas')
    canvas.width = IMAGE_SIDE
    canvas.height = IMAGE_SIDE
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    const side = Math.min(bitmap.width, bitmap.height)
    ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, IMAGE_SIDE, IMAGE_SIDE)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    return blob ?? file
  } catch {
    return file
  } finally {
    bitmap.close()
  }
}

/** What goes on the wire; the size cap is checked here so an oversize original fails instantly. */
export async function prepareImageUpload(file: File): Promise<Blob> {
  const blob = await downscaleImage(file)
  if (blob.size > MAX_IMAGE_BYTES) {
    throw new ApiError(413, 'IMAGE_TOO_LARGE', { max_bytes: MAX_IMAGE_BYTES, max_mb: MAX_IMAGE_MB })
  }
  return blob
}
